import { ChangeDetectionStrategy, Component, inject, OnDestroy, signal } from '@angular/core';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';
import { BrandFetchService, BrandSuggestionResponse } from '../../../onboarding/service/brand-fetch/brand-fetch.service';
import { brandInitial } from '../../../onboarding-value-first/services/value-first-onboarding.types';
import { ValueFirstAgencyService } from '../../services/value-first-agency.service';
import { AgencyRepresentedBrand, AGENCY_BRAND_CAP } from '../../services/value-first-agency.types';

type BrandSearchResult =
  | { status: 'cleared' }
  | { status: 'failed' }
  | { status: 'done'; results: BrandSuggestionResponse[] };

@Component({
  selector: 'vfa-brands-step',
  templateUrl: './brands-step.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VfaBrandsStep implements OnDestroy {
  readonly onboarding = inject(ValueFirstAgencyService);
  private readonly brandFetch = inject(BrandFetchService);
  readonly cap = AGENCY_BRAND_CAP;
  /** `null` while idle or after a failed search. An empty array means the search completed with no hits. */
  readonly suggestions = signal<BrandSuggestionResponse[] | null>(null);
  readonly manualOpen = signal(false);
  readonly manualName = signal('');
  readonly manualWebsite = signal('');

  private readonly querySubject = new Subject<string>();
  private readonly subscriptionList = new Subscription();

  constructor() {
    this.subscriptionList.add(
      this.querySubject.pipe(
        debounceTime(250),
        switchMap((query): Observable<BrandSearchResult> => {
          const trimmed = query.trim();
          if (trimmed.length <= 3) {
            return of({ status: 'cleared' });
          }
          return this.brandFetch.brandSearch(trimmed).pipe(
            map((results) => ({ status: 'done' as const, results })),
            catchError(() => of({ status: 'failed' as const })),
          );
        }),
      ).subscribe((outcome) => {
        console.log('👀👀👀 outcome: ', outcome);
        if (this.manualOpen() || this.onboarding.draft().brandUrl.trim().length <= 3 || outcome.status !== 'done') {
          this.suggestions.set(null);
          return;
        }
        this.suggestions.set(outcome.results);
      }),
    );
  }

  initial(name: string): string {
    return brandInitial(name);
  }

  domainLabel(domain: string): string {
    return domain || 'Added manually';
  }

  removeLabel(brand: AgencyRepresentedBrand): string {
    return `Remove ${brand.domain || brand.name}`;
  }

  onBrandUrl(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.onboarding.setBrandUrl(value);
    if (value.trim().length <= 3) {
      this.suggestions.set(null);
    }
    this.querySubject.next(value);
  }

  select(suggestion: BrandSuggestionResponse): void {
    console.log('👀👀👀 select: ', suggestion);
    this.suggestions.set(null);
    void this.onboarding.addBrandFromSuggestion(suggestion);
  }

  add(event: Event): void {
    event.preventDefault();
    this.suggestions.set(null);
    void this.onboarding.addBrand();
  }

  openManual(): void {
    this.suggestions.set(null);
    this.onboarding.clearError();
    this.manualOpen.set(true);
  }

  closeManual(): void {
    this.onboarding.clearError();
    this.manualOpen.set(false);
  }

  onManualName(event: Event): void {
    this.manualName.set((event.target as HTMLInputElement).value);
    this.onboarding.clearError();
  }

  onManualWebsite(event: Event): void {
    this.manualWebsite.set((event.target as HTMLInputElement).value);
    this.onboarding.clearError();
  }

  submitManual(event: Event): void {
    event.preventDefault();
    const added = this.onboarding.addBrandManually(this.manualName(), this.manualWebsite());
    if (!added) {
      return;
    }
    this.manualName.set('');
    this.manualWebsite.set('');
    this.manualOpen.set(false);
  }

  remove(id: string): void {
    this.onboarding.removeBrand(id);
  }

  continueBrands(): void {
    this.onboarding.continueWithBrands();
  }

  skip(): void {
    this.onboarding.skipBrands();
  }

  continueLabel(): string {
    const count = this.onboarding.draft().brands.length;
    if (count === 0) {
      return 'Continue →';
    }
    return count === 1 ? 'Continue with 1 brand →' : `Continue with ${count} brands →`;
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
    this.querySubject.complete();
  }
}
